/**
 * Validates whether a given string is a valid email address.
 *
 * This function uses a strict regular expression to validate email addresses
 * according to standard email formatting rules (RFC 5322-compliant). It ensures
 * the email contains proper local and domain parts and supports a wide range
 * of valid email patterns while excluding invalid ones.
 *
 * @param email - The string to be checked as an email address.
 *
 * @returns A boolean value indicating whether the provided string is a valid
 * email address.
 *
 * @example
 * ```typescript
 * // Returns true for a valid email address
 * const isValid = isEmailValid('example@test.com'); // true
 *
 * // Returns false for an invalid email address
 * const isInvalid = isEmailValid('invalid-email'); // false
 * ```
 *
 * @remarks
 * - The regular expression used is intentionally strict to minimize false positives.
 * - This function does not validate email existence, only format validity.
 *
 * @throws This function does not throw any exceptions.
 *
 * @see {@link https://tools.ietf.org/html/rfc5322 | RFC 5322 Official Specification}
 * for more details on email formatting rules.
 */
export const isEmailValid = (email: string): boolean => {
  const strictEmailRegex = new RegExp(
    `^[A-Za-z0-9!#$%&'*+/=?^_\`{|}~-]+(?:\\.[A-Za-z0-9!#$%&'*+/=?^_\`{|}~-]+)*@` +
      `(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\\.)+[A-Za-z]{2,}$`
  );

  return strictEmailRegex.test(email);
};
